/**
 * 동기화 서비스: 잠금 해제된 세션 + Google 토큰 → 암호화 동기화.
 * 같은 기기의 여러 탭이 동시에 동기화하지 않도록 Web Locks로 직렬화한다.
 */
import { META } from '@/data/db/schema';
import { SyncEngine, type SyncReport } from '@/data/sync/engine';
import { GoogleDriveStore } from '@/data/sync/googleDrive';
import { resolveConflict } from '@/data/sync/join';
import type { RemoteStore } from '@/data/sync/remote';
import type { SyncConflict } from '@/domain/schema/v1';
import { connectGoogle, disconnectGoogle, getAccessToken } from '@/platform/googleAuth';
import { saveRefreshToken } from '@/data/sync/tokenStore';
import type { AppSession } from './appSession';

export type SyncStatus = { lastSyncAt: string | null; lastError: string | null; report?: SyncReport };

export function deviceLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) return 'iPhone';
  if (/Android/.test(ua)) return 'Android';
  if (/Windows/.test(ua)) return 'Windows PC';
  if (/Mac/.test(ua)) return 'Mac';
  return '기기';
}

export const driveStore = (): RemoteStore => new GoogleDriveStore(getAccessToken);

const ERROR_TEXT: Record<string, string> = {
  RemoteAuthError: 'Google 연결이 만료되었습니다. 다시 연결해 주세요.',
  RemoteUnavailableError:
    '네트워크 또는 Google Drive에 연결할 수 없습니다. 입력한 내용은 이 기기에 안전하게 저장되어 있습니다.',
  VaultMismatchError:
    'Drive에 다른 데이터(다른 vault)가 있습니다. 새 기기라면 첫 화면의 "Drive에서 가져오기"를 사용하세요.',
  NewerRemoteError: '다른 기기의 앱이 더 새 버전입니다. 이 기기의 앱을 업데이트하세요.',
  IntegrityError: 'Drive의 동기화 파일을 열 수 없습니다 (손상 또는 다른 키). 로컬 데이터는 그대로입니다.',
};
export const syncErrorText = (e: unknown) =>
  ERROR_TEXT[(e as Error)?.name] ?? '동기화에 실패했습니다. 로컬 데이터는 그대로입니다.';

export async function runSync(session: AppSession, remote: RemoteStore = driveStore()): Promise<SyncReport> {
  const exec = async () => {
    try {
      const engine = new SyncEngine({ ...session.ctx, repo: session.repo, remote, deviceLabel: deviceLabel() });
      return await engine.sync();
    } catch (e) {
      const prev = (await session.db.meta.get(META.syncStatus))?.value as SyncStatus | undefined;
      await session.db.meta.put({
        key: META.syncStatus,
        value: { lastSyncAt: prev?.lastSyncAt ?? null, lastError: (e as Error)?.name ?? 'Error' },
      });
      throw e;
    }
  };
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  return locks ? locks.request('counselai-sync', exec) : exec();
}

export async function getSyncStatus(session: AppSession): Promise<SyncStatus> {
  return (
    ((await session.db.meta.get(META.syncStatus))?.value as SyncStatus | undefined) ?? {
      lastSyncAt: null,
      lastError: null,
    }
  );
}

export async function isDriveConnected(session: AppSession): Promise<boolean> {
  return !!(await session.db.meta.get(META.driveConnected))?.value;
}

export async function setDriveConnected(session: AppSession, on: boolean): Promise<void> {
  await session.db.meta.put({ key: META.driveConnected, value: on });
}

/**
 * 사용자 클릭 안에서 호출. 처음 한 번 승인하면 갱신 토큰을 암호화 저장해 이후 계속 연결 상태를 유지한다.
 * @returns 계속 연결(자동 갱신)이 설정되었는지
 */
export async function connectDrive(session: AppSession): Promise<boolean> {
  const refresh = await connectGoogle();
  if (refresh) await saveRefreshToken(session.ctx, refresh);
  await setDriveConnected(session, true);
  return !!refresh;
}

export async function disconnectDrive(session: AppSession): Promise<void> {
  disconnectGoogle();
  await saveRefreshToken(session.ctx, null);
  await setDriveConnected(session, false);
}

export async function pendingChanges(session: AppSession): Promise<number> {
  return session.db.outbox.count();
}

export async function openConflicts(session: AppSession): Promise<SyncConflict[]> {
  return (await session.repo.list('SyncConflict')).filter((c) => c.resolution === 'unresolved');
}

export function resolve(session: AppSession, conflictId: string, choice: 'local' | 'remote') {
  return resolveConflict(session.repo, session.db, conflictId, choice);
}
